import React, { PropsWithChildren, useCallback, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFocusEffect } from '@react-navigation/native';

import { CustomBottomSheetModalProvider } from '@/feature/alerts/components/CustomBottomSheetModalProvider';
import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { authUserKeys } from '@/hooks/use-auth-user-config';
import { Product, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { logger } from '@/utils/logging';
import { useQueryClient } from '@tanstack/react-query';

import { TermsAndConditionsModal, TermsAndConditionsModalRef } from '../TermsAndConditionsModal';
import { useUserAcceptProductTerms } from '../hooks/use-user-accept-product-terms';
import { LOG_TAG, TermsContentRenderer } from '../utils/terms-and-conditions-utils';

// Products that have Terms and Conditions implemented
export const PRODUCTS_WITH_TERMS_AND_CONDITIONS = new Set([
    Product.Pickem,
    Product.Arcade,
    Product.Sweeps,
    Product.Prediction,
    Product.Rips,
]);

/**
 * Check if a product has Terms and Conditions implementation
 */
export const hasTermsAndConditionsSupport = (product: Product): boolean => {
    return PRODUCTS_WITH_TERMS_AND_CONDITIONS.has(product);
};

interface ShowTermsAndConditionsOptions {
    onSuccess?: () => void;
    onCancel?: () => void;
    onWebViewOpened?: () => void;
    onTermsError?: (error: unknown) => void;
    // replaces the default checkbox body with custom terms content.
    renderContent?: TermsContentRenderer;
}

interface TermsAndConditionsContextValue {
    showTermsAndConditionsModal: (options?: ShowTermsAndConditionsOptions) => void;
}

const TermsAndConditionsModalContext = React.createContext<TermsAndConditionsContextValue | undefined>(undefined);

/**
 * Provides access to the Modal used to display terms and conditions
 */
export const TermsAndConditionsModalProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const modalRef = useRef<TermsAndConditionsModalRef>(null);

    const showTermsAndConditionsModal = useCallback((options?: ShowTermsAndConditionsOptions) => {
        modalRef.current?.show(options);
    }, []);

    return (
        <CustomBottomSheetModalProvider>
            <TermsAndConditionsModalContext.Provider value={{ showTermsAndConditionsModal }}>
                {children}
                <TermsAndConditionsModal ref={modalRef} />
            </TermsAndConditionsModalContext.Provider>
        </CustomBottomSheetModalProvider>
    );
};

const useTermsAndConditionsContent = () => {
    const context = useContext(TermsAndConditionsModalContext);
    if (context === undefined) {
        throw new Error('useTermsAndConditionsContent must be used within a TermsAndConditionsModalProvider');
    }
    return context;
};

/**
 * Hook to display Terms and Conditions modal and handle auto-reopening when user returns
 * from opening terms and conditions webview link.
 */
export const useTermsAndConditionsModal = () => {
    const { showTermsAndConditionsModal } = useTermsAndConditionsContent();
    const [pendingOptions, setPendingOptions] = useState<ShowTermsAndConditionsOptions | undefined>(undefined);
    const shouldReopenRef = useRef(false);
    const acceptProductTermsMutation = useUserAcceptProductTerms();
    const queryClient = useQueryClient();
    const { showToast } = useAlerts();
    const { t } = useTranslation('terms_and_conditions');

    // Handle reopening modal when returning from webview
    useFocusEffect(
        useCallback(() => {
            if (shouldReopenRef.current && pendingOptions) {
                shouldReopenRef.current = false;
                showTermsAndConditionsModal(pendingOptions);
                pendingOptions;
            }
        }, [showTermsAndConditionsModal, pendingOptions])
    );

    const resetState = useCallback(() => {
        setPendingOptions(undefined);
        shouldReopenRef.current = false;
    }, []);

    const createEnhancedOptions = useCallback(
        (options?: ShowTermsAndConditionsOptions) => {
            return {
                ...options,
                onWebViewOpened: () => {
                    shouldReopenRef.current = true;
                    options?.onWebViewOpened?.();
                },
                onSuccess: async () => {
                    resetState();
                    try {
                        await acceptProductTermsMutation.mutateAsync();
                        await queryClient.invalidateQueries({ queryKey: authUserKeys.config() });
                    } catch (error) {
                        logger.error(
                            LOG_TAG,
                            `Failed to accept ${useJurisdictionStore.getState().product} terms and conditions`,
                            error
                        );
                        // Only show toast if there is no custom error handler provided
                        if (options?.onTermsError) {
                            options.onTermsError(error);
                        } else {
                            showToast({ message: t('accept_terms_failed'), toastType: 'warning' });
                        }
                        return;
                    }
                    options?.onSuccess?.();
                },
                onCancel: () => {
                    resetState();
                    options?.onCancel?.();
                },
            };
        },
        [resetState, acceptProductTermsMutation, queryClient, showToast, t]
    );

    return {
        showTermsAndConditionsModal: useCallback(
            (options?: ShowTermsAndConditionsOptions) => {
                const enhancedOptions = createEnhancedOptions(options);
                setPendingOptions(enhancedOptions);
                showTermsAndConditionsModal(enhancedOptions);
            },
            [showTermsAndConditionsModal, createEnhancedOptions]
        ),
    };
};
